import React from 'react';

import { useIntl } from 'react-intl-next';

import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import {
	sharedPluginStateHookMigratorFactory,
	useSharedPluginState,
} from '@atlaskit/editor-common/hooks';
import { type Command } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AIProactivePluginConfig,
	AIProactivePluginInjectionAPI,
} from '../../../aiProactivePluginType';
import { type Recommendation } from '../../../pm-plugins/ai-proactive/api';
import {
	clearHoverRecommendation,
	hoverRecommendation,
	insertRecommendation,
	removeRecommendation,
	toggleSetting,
	clearSelectRecommendation,
} from '../../../pm-plugins/ai-proactive/commands';
import {
	closeProactiveAISuggestionDisplayWithAnalytics,
	selectRecommendationWithAnalytics,
	toggleProactiveAISuggestionDisplayWithAnalytics,
} from '../../../pm-plugins/ai-proactive/commands-with-analytics';
import { SETTING_KEYS, SUGGESTIONS_PANEL_WIDTH } from '../../../pm-plugins/ai-proactive/constants';
import { getPluginState as getProactivePluginState } from '../../../pm-plugins/ai-proactive/plugin-factory';

import { ContextPanelAfterElement } from './heading-item';
import { RecommendationPanel } from './recommendation-panel';

// Remove below function while cleaning platform_editor_ai_object_sidebar_injection
export const getContextPanel =
	(config: AIProactivePluginConfig, getEditorView?: () => EditorView | undefined) =>
	(api: AIProactivePluginInjectionAPI) =>
	(state: EditorState) => {
		const proactivePluginState = getProactivePluginState(state);
		if (!proactivePluginState) {
			return null;
		}
		const { isProactiveEnabled, isProactiveContextPanelOpen } = proactivePluginState;
		if (!isProactiveEnabled || !isProactiveContextPanelOpen) {
			return null;
		}
		const editorView = getEditorView?.();
		return <ContextPanel api={api} editorView={editorView} config={config} />;
	};

type ContextPanelProps = {
	api: AIProactivePluginInjectionAPI;
	editorView: EditorView | undefined;
	config: AIProactivePluginConfig;
};

const useSharedState = sharedPluginStateHookMigratorFactory(
	(api: AIProactivePluginInjectionAPI | undefined) => {
		const recommendations = useSharedPluginStateSelector(api, 'aiProactive.recommendations');
		const selectedRecommendationId = useSharedPluginStateSelector(
			api,
			'aiProactive.selectedRecommendationId',
		);
		const hoveredRecommendationId = useSharedPluginStateSelector(
			api,
			'aiProactive.hoveredRecommendationId',
		);
		const displayAllSuggestions = useSharedPluginStateSelector(
			api,
			'aiProactive.displayAllSuggestions',
		);
		const isLoading = useSharedPluginStateSelector(api, 'aiProactive.isLoading');
		const isEmptyDocument = useSharedPluginStateSelector(api, 'aiProactive.isEmptyDocument');
		const hasNoMoreSuggestions = useSharedPluginStateSelector(
			api,
			'aiProactive.hasNoMoreSuggestions',
		);
		const editorDisabled = useSharedPluginStateSelector(api, 'editorDisabled.editorDisabled');
		return {
			recommendations,
			selectedRecommendationId,
			hoveredRecommendationId,
			displayAllSuggestions,
			isLoading,
			isEmptyDocument,
			hasNoMoreSuggestions,
			editorDisabled,
		};
	},
	(api: AIProactivePluginInjectionAPI | undefined) => {
		const { aiProactiveState } = useSharedPluginState(api, ['aiProactive']);
		const { editorDisabledState } = useSharedPluginState(api, ['editorDisabled']);
		return {
			recommendations: aiProactiveState?.recommendations,
			selectedRecommendationId: aiProactiveState?.selectedRecommendationId,
			hoveredRecommendationId: aiProactiveState?.hoveredRecommendationId,
			displayAllSuggestions: aiProactiveState?.displayAllSuggestions,
			isLoading: aiProactiveState?.isLoading,
			isEmptyDocument: aiProactiveState?.isEmptyDocument,
			hasNoMoreSuggestions: aiProactiveState?.hasNoMoreSuggestions,
			editorDisabled: editorDisabledState?.editorDisabled,
		};
	},
);

const ContextPanel = ({ api, editorView, config }: ContextPanelProps) => {
	const analyticsApi = api?.analytics?.actions;
	const { locale } = useIntl();
	const {
		recommendations,
		selectedRecommendationId,
		hoveredRecommendationId,
		displayAllSuggestions,
		isLoading,
		isEmptyDocument,
		hasNoMoreSuggestions,
		editorDisabled,
	} = useSharedState(api);

	// if we don't check for editorView here
	// typescript has issues with editorView maybe
	// being undefined further down
	if (!recommendations || !editorView) {
		return null;
	}

	const proactivePluginState = getProactivePluginState(editorView.state);
	const { documentChecker } = proactivePluginState;

	const triggeredFrom = 'contextPanel';
	const runCommand = (command: Command) => {
		const { state, dispatch } = editorView;
		command(state, dispatch);
	};

	const onRecommendationApply = ({ recommendation }: { recommendation: Recommendation }) => {
		runCommand(
			insertRecommendation({
				analyticsApi,
				recommendationId: recommendation.id,
				triggeredFrom,
				insertionMethod: 'replace',
			}),
		);
	};

	const onRecommendationDismiss = ({ recommendation }: { recommendation: Recommendation }) => {
		runCommand(
			removeRecommendation({
				analyticsApi,
				recommendationId: recommendation.id,
				triggeredFrom,
				feedback: {
					product: config.product,
					locale,
					editorView,
					handleFeedbackSubmission: config.handleFeedbackSubmission,
				},
			}),
		);
	};

	const onRecommendationSelect = ({ recommendation }: { recommendation: Recommendation }) => {
		runCommand(selectRecommendationWithAnalytics(analyticsApi)(recommendation.id));
	};

	const onRecommendationHover = ({ recommendation }: { recommendation: Recommendation }) => {
		runCommand(hoverRecommendation(recommendation.id));
	};

	const onRecommendationLeave = ({ recommendation }: { recommendation: Recommendation }) => {
		runCommand(clearHoverRecommendation());
	};

	const onPanelClose = () => {
		if (fg('platform_editor_ai_object_sidebar_injection')) {
			runCommand(closeProactiveAISuggestionDisplayWithAnalytics(analyticsApi)(triggeredFrom));
		} else {
			runCommand(toggleProactiveAISuggestionDisplayWithAnalytics(analyticsApi)(triggeredFrom));
		}
	};

	const onSpellingGrammar = () => {};

	// Ignored via go/ees005
	// eslint-disable-next-line require-await
	const onReloadSuggestions = async () => {
		if (documentChecker) {
			documentChecker.refresh();
		}
	};

	const onToggleSuggestions = () => {
		runCommand(toggleSetting(SETTING_KEYS.ALL_SUGGESTIONS));
	};

	const onClearSelectRecommendation = () => {
		runCommand(clearSelectRecommendation());
	};

	const onAskAI = () => {
		api?.aiExperience?.actions?.openAIModal({ invokedFrom: 'proactiveAIEmptyState' });
		onPanelClose();
	};

	return (
		<RecommendationPanel
			isSpellingGrammarEnabled={false}
			spellingGrammarErrorCount={0}
			recommendations={recommendations}
			hoveredRecommendationId={hoveredRecommendationId}
			selectedRecommendationId={selectedRecommendationId}
			onRecommendationApply={onRecommendationApply}
			onRecommendationDismiss={onRecommendationDismiss}
			onRecommendationSelect={onRecommendationSelect}
			onRecommendationHover={onRecommendationHover}
			onRecommendationLeave={onRecommendationLeave}
			onSpellingGrammar={onSpellingGrammar}
			onReloadSuggestions={onReloadSuggestions}
			isEditorDisabled={editorDisabled}
			isLoading={Boolean(isLoading)}
			onClearSelectRecommendation={onClearSelectRecommendation}
			onPanelClose={onPanelClose}
			config={config}
			editorView={editorView}
			displayAllSuggestions={Boolean(displayAllSuggestions)}
			isEmptyDocument={Boolean(isEmptyDocument)}
			hasNoMoreSuggestions={Boolean(hasNoMoreSuggestions)}
			onToggleSuggestions={onToggleSuggestions}
			onAskAI={onAskAI}
		/>
	);
};

export const createToggleSuggestionsPanel =
	(
		api: AIProactivePluginInjectionAPI,
		config: AIProactivePluginConfig,
		editorViewRef: Record<'current', EditorView | undefined>,
	) =>
	(triggeredFrom: string) => {
		const editorView = editorViewRef.current;
		const showContextPanel = api?.contextPanel?.actions?.showPanel;
		const closeContextPanel = api?.contextPanel?.actions?.closePanel;

		if (!editorView || !showContextPanel || !closeContextPanel) {
			return false;
		}

		const pluginState = getProactivePluginState(editorView.state);
		if (!pluginState || !pluginState.isProactiveEnabled) {
			return false;
		}

		if (pluginState?.isProactiveContextPanelOpen) {
			/**
			 * Calling closeContextPanel will close the panel and also call onPanelClose.
			 * That's why don't need to fire closeProactiveAISuggestionDisplay command.
			 */
			closeContextPanel();
		} else {
			const ContextPanelWrapper = () => {
				return <ContextPanel api={api} editorView={editorView} config={config} />;
			};
			const ContextPanelAfterElementWrapper = () => {
				return <ContextPanelAfterElement editorView={editorView} config={config} />;
			};

			/**
			 * On panel close will be called when:
			 * 	- close button is clicked
			 * 	- closeContextPanel is called (it callsed when suggestions button is clicked and panel is already open)
			 */
			const onPanelClose = () => {
				closeProactiveAISuggestionDisplayWithAnalytics(api?.analytics?.actions)('contextPanel')(
					editorView.state,
					editorView.dispatch,
				);
			};

			showContextPanel(
				{
					id: 'proactive-ai-panel',
					headerComponentElements: {
						headerLabel: aiProactiveMessages.suggestionsTitle,
						HeaderRightAlignedElement: ContextPanelAfterElementWrapper,
					},
					BodyComponent: ContextPanelWrapper,
					closeOptions: {
						onPanelClose,
					},
				},
				'push',
				SUGGESTIONS_PANEL_WIDTH,
			);

			toggleProactiveAISuggestionDisplayWithAnalytics(api?.analytics?.actions)(triggeredFrom)(
				editorView.state,
				editorView.dispatch,
			);
		}
	};
